import { ITag } from '../../model/tagging/Tag';
import { Tag } from '../../model/tagging/Tag';
import { SearchExpression } from '../../model/search/advanced/SearchExpression';
import { Service } from '../Service';

export class TagService extends Service<any> {
  constructor(resource: any, rootScope: IRootScope, private configService, resourceWrapperService, private httpService, private notificationService) {
    super({resource, rootScope, resourceWrapperService});

    let tagResource = this.resource(`${this.configService.getServerBaseUrlForResource()}/project/:projectId/tag/:id`,
      {id: "@id", "projectId": "@projectId"},
      {
        update: {method: "PUT"},
        query: {method: "GET", params: {page: 1, limit: 20}, isArray: false}
      }
    );

    this.itemResource = this.resourceWrapperService.WrapActions(tagResource);

    // TaggingComplete / UntaggingComplete / TaggingUpdate are handled in React
    // (src/hooks/useProjectTags -> WebSocketBridge), which also re-broadcasts the
    // tags.fetch / tags.complete events the Angular review pages listen for.
  }

  public FindMany(projectId: string, ids: string[], callback: Function) {
    let url = `${this.configService.getServerBaseUrl()}/project/${projectId}/tags/${ids.join(",")}`;

    return this.httpService.$http({
      method: "GET",
      url: url
    }).success((response, status) => callback && callback(response));
  }

  public GetAll(projectId: string, categoryId: string, page: number, limit: number, callback: Function, assignedOnly: boolean = false) {
    return this.itemResource.query({
      projectId: projectId,
      categoryId: categoryId,
      page: page,
      limit: limit,
      assignedOnly
    }, (tags) => callback && callback(tags));
  }

  public FindByName(projectId: string, tagName: string) {
    let url = `${this.configService.getServerBaseUrl()}/project/${projectId}/tag/name/${tagName}`;
    return this.httpService.$http({
      method: "GET",
      url: url
    });
  }

  public async DeleteTag(tag: ITag) {
    let tagResource = this.AsResource(tag);
    tagResource.$delete().then(() => {
      this.rootScope.$broadcast("tags.complete");
    }).catch(() => this.notificationService.addError({
      title: "Error deleting tag",
      description: "That's embarrassing, please try again later",
      timeout: 5000
    }));
  }

  public TagResource(project, collection: any, resource: any, tagName: string, tagRelated: boolean, callback?: Function) {
    let url = `${this.configService.getServerBaseUrl()}/project/${project.id}/collection/${collection.id}/resource/${resource.id}/tag`;

    return this.httpService.$http({
      method: "POST",
      url: url,
      data: {name: tagName, tagRelated: tagRelated}
    }).success((response, status) => {
      this.FindResourceTags(project.id, collection.id, resource.id, (tags) => resource.tags = tags);

      this.rootScope.$broadcast("tags.complete");
      if (callback) callback(response);
    }).error((data, status) => {
      this.notificationService.addError({
        title: 'Error tagging',
        description: "That's embarrassing, please try again later",
        timeout: 5000
      });
    });
  }

  public TagResources(project, resources: Array<any>, tagName: string, tagRelated: boolean, callback?: Function) {
    let url = `${this.configService.getServerBaseUrl()}/project/${project.id}/resources/tag`;

    return this.httpService.$http({
      method: "POST",
      url: url,
      data: {name: tagName, tagRelated: tagRelated, resources}
    }).success((response, status) => {
      this.rootScope.$broadcast("tags.complete");
      if (callback) callback(response);
    }).error((data, status) => {
      this.notificationService.addError({
        title: 'Error tagging',
        description: "That's embarrassing, please try again later",
        timeout: 5000
      });
    });
  }

  public async TagConversation(project, collection: any, resource, chatMessageId: string, tagName: string) {
    let url = `${this.configService.getServerBaseUrl()}/project/${project.id}/collection/${collection.id}/chatMessageId/${chatMessageId}/tag`;

    return this.httpService.$http({
      method: "POST",
      url: url,
      data: {name: tagName, tagRelated: false}
    }).error((data, status) => {
      this.notificationService.addError({
        title: 'Error tagging',
        description: "That's embarrassing, please try again later",
        timeout: 5000
      });

      this.rootScope.$broadcast("tags.complete");
    });
  }

  public async GetConversationTags(project, collection: any, resource, chatMessageId: string) {
    let url = `${this.configService.getServerBaseUrl()}/project/${project.id}/collection/${collection.id}/chatMessageId/${chatMessageId}/tags`;

    return this.httpService.$http({
      method: "GET",
      url: url
    }).error((data, status) => {
      this.notificationService.addError({
        title: 'Failed to get tags.',
        description: "That's embarrassing, please try again later",
        timeout: 5000
      });
    });
  }

  public TagCollections(project, collections: any[], filter: SearchExpression, columnFilters: SearchExpression, tagName: string, tagRelated: boolean, callback: Function) {
    let collectionIds = _.pluck(collections, "id").join(',');
    let url = `${this.configService.getServerBaseUrl()}/project/${project.id}/collection/${collectionIds}/tag`;

    return this.httpService.$http({
      method: "POST",
      url: url,
      data: {filter, columnFilters, name: tagName, tagRelated: tagRelated}
    }).success((response, status) => callback && callback(response)).error((data, status) => {
      this.notificationService.addError({
        title: "Error tagging",
        description: "That's embarrassing, please try again later",
        timeout: 5000
      });

      this.rootScope.$broadcast("tags.complete");
      if (callback) callback();
    });
  }

  public TagCollectionSearch(project, collection: any, searchExpression, columnFilters: SearchExpression, tagName: string, tagRelated: boolean, callback: Function) {
    let url = `${this.configService.getServerBaseUrl()}/project/${project.id}/collection/${collection.id}/tag/search`;

    return this.httpService.$http({
      method: "POST",
      url: url,
      params: {q: searchExpression instanceof SearchExpression ? "" : searchExpression},
      data: {
        name: tagName,
        tagRelated: tagRelated,
        searchExpression: searchExpression instanceof SearchExpression ? searchExpression : null,
        columnFilters
      }
    }).success((response, status) => callback && callback(response)).error((data, status) => {
      this.notificationService.addError({
        title: "Error tagging",
        description: "That's embarrassing, please try again later",
        timeout: 5000
      });

      this.rootScope.$broadcast("tags.complete");
      if (callback) callback();
    });
  }

  public TagProjectSearch(project, searchExpression, columnFilters: SearchExpression, tagName: string, tagRelated: boolean, callback: Function) {
    let url = `${this.configService.getServerBaseUrl()}/project/${project.id}/tag/search`;

    return this.httpService.$http({
      method: "POST",
      url: url,
      params: {q: searchExpression instanceof SearchExpression ? "" : searchExpression},
      data: {
        name: tagName,
        tagRelated: tagRelated,
        searchExpression: searchExpression instanceof SearchExpression ? searchExpression : null,
        columnFilters
      },
    }).success((response, status) => callback && callback(response)).error((data, status) => {
      this.notificationService.addError({
        title: 'Error tagging',
        description: "That's embarrassing, please try again later",
        timeout: 5000
      });

      this.rootScope.$broadcast("tags.complete");
      if (callback) callback(data);
    });
  }

  public UntagResource(project, collection: any, resource, chatMessageId: string, tag: ITag, callback: Function, withRelated = false) {
    let url = `${this.configService.getServerBaseUrl()}/project/${project.id}/collection/${collection.id}/resource/${resource.id}/tag/${tag.id}`;

    return this.httpService.$http({
      method: "DELETE",
      url: url,
      data: "",
      params: {withRelated, chatMessageId},
    }).success((response, status) => {
      this.FindResourceTags(project.id, collection.id, resource.id, (tags) => resource.tags = tags);
      this.rootScope.$broadcast("tags.complete");
      if (callback) callback(tag);
    }).error((data, status) => {
      this.notificationService.addError({
        title: "Error removing tag",
        description: "That's embarrassing, please try again later",
        timeout: 5000
      });
      if (callback) callback(tag);
    });
  }

  public GetSuggestions(projectId: string, tagName: string, categoryBitmask: number, checkInverse = false, hasInverse = false, isChild = false, includeSubTags = false) {
    let url = `${this.configService.getServerBaseUrl()}/project/${projectId}/tag/suggest`;

    // 7 all,
    // 4 only project
    return this.httpService.$http({
      method: "GET",
      url: url,
      params: {
        tagName: tagName,
        categoryBitmask: categoryBitmask,
        limit: 15,
        hasInverse: hasInverse,
        checkInverse: checkInverse,
        isChild: isChild,
        includeSubTags
      }
    });
  }

  public CreateText(tag: ITag) {
    let url = `${this.configService.getServerBaseUrl()}/project/${tag.projectId}/tag/${tag.id}/text`;

    return this.httpService.$http({method: "POST", url: url, data: {level: 1}}).success((response, status) => {
      this.notificationService.addSuccess({
        title: "Extracting Text started",
        description: "",
        timeout: 2500
      });
    }).error((data, status) => {
      this.notificationService.addError({
        title: "Error extracting text",
        description: "That's embarrassing, please try again later",
        timeout: 5000
      });
    });
  }

  public FindResourceTags(projectId: string, collectionId: string, resourceId: string, callback: Function) {
    let url = `${this.configService.getServerBaseUrl()}/project/${projectId}/collection/${collectionId}/resource/${resourceId}/tags`;

    return this.httpService.$http({
      method: "GET",
      url: url
    }).success((response, status) => callback && callback(response));
  }

  public CreateTag = (projectId) => new Tag(projectId);
}

frontendApp.factory("TagService", ['$resource', '$rootScope', 'ConfigService', 'ResourceWrapperService', 'HttpService', 'NotificationService', function(resource, rootScope, configService, resourceWrapperService, httpService, notificationService) {
  return new TagService(resource, rootScope, configService, resourceWrapperService, httpService, notificationService);
}]);
